// Define the base/default configuration
const defaultConfig = {
  // Organisation-wide sign-in policy. Defaults to hybrid (password + Microsoft SSO).
  //
  // Terminology:
  // - 'with_microsoft_sso' means: "Microsoft-first sign-in UX, but we still provision
  //   an email/password credential as an emergency break-glass fallback."
  //
  // Valid values: 'password' | 'with_microsoft_sso' | 'hybrid'
  authMode: 'hybrid',
  steps: {
    '1': {
      title: "User Details",
      fields: {
        '1': {
          varName: "title",
          displayText: "Professional Title (if applicable)",
          inputType: "selectOneDropdown",
          options: [
            { value: "Dr.", label: "Dr." },
            { value: "Prof.", label: "Prof." },
            { value: "Mr.", label: "Mr." },
            { value: "Mrs.", label: "Mrs." },
            { value: "Ms.", label: "Ms." },
            { value: "Miss", label: "Miss" },
            { value: "N/A", label: "N/A" },
          ],
          required: true,
        },
        '2': {
          varName: "firstName",
          displayText: "First Name",
          inputType: "freeText",
          placeholder: "Enter first name",
          required: true,
        },
        '3': {
          varName: "lastName",
          displayText: "Last Name",
          inputType: "freeText",
          placeholder: "Enter last name",
          required: true,
        },
        '4': {
          varName: "email",
          displayText: "Email Address",
          inputType: "freeText",
          placeholder: "Enter email that will be used to log in",
          required: true,
        },   
      }
    },
    '2': {
      title: "User Type",
      fields: {                    
        '1': {
          varName: "userType",
          displayText: "User Type",
          inputType: "selectOneDropdown",
          options: [
            { value: "Clinician", label: "Clinician" },
            { value: "Support", label: "Support" },
            // We intentionally split Org. Admin into two *UI* variants:
            // - Clinical Org. Admin: can be assigned to patients as a clinician
            // - Non-Clinical Org. Admin: can manage staff/patients but must not be assignable
            //
            // Important: In the backend we still store BOTH variants as userType = 'Org. Admin'
            // (to avoid sweeping permission/rules changes). The clinical/non-clinical distinction
            // is carried in a separate boolean flag: isAssignableClinician.
            { value: "Clinical Org. Admin", label: "Clinical Org. Admin" },
            { value: "Non-Clinical Org. Admin", label: "Non-Clinical Org. Admin" },
          ],
          required: true,
        },
        '2': {
          varName: "role",
          displayText: "Role",
          inputType: "freeText",
          placeholder: "Enter role as it will appear in documents",
          // The job title printed on letters and reports. Required for every
          // new provider user, including Support and non-clinical admins.
          // Registration (the next step) stays optional. The same rule is
          // enforced on the server, so this flag is not the only gate.
          // 120 matches MAX_PROVIDER_ROLE_LENGTH in providerRole.js.
          description: "This is the job title that appears on letters and reports.",
          maxLength: 120,
          required: true,
          isCustomField: true,
        },
      }
    },
    '3': {
      title: "Professional Registration",
      fields: {
        '1': {
          varName: "registrationBody",
          displayText: "Registration Body",
          inputType: "selectOneDropdown",
          options: [
            { value: "GMC", label: "General Medical Council (GMC)" }, // 7-digit number (e.g., 7456123)No letters or special characters. 
            { value: "HCPC", label: "Health and Care Professions Council (HCPC)" }, // A unique alphanumeric code Two-letter profession code followed by a series of numbers (e.g., PH123456, SL987654) The prefix indicates the profession (e.g., PH = Physiotherapist, SL = Speech and Language Therapist)
            { value: "NMC", label: "Nursing and Midwifery Council (NMC)" }, // An 8-digit number (e.g., 15A1234B) May include letters in the third and eighth positions
            { value: "GPhC", label: "General Pharmaceutical Council (GPhC)" }, // Numerical format: A 7-digit number (e.g., 2045678). No letters or special characters.
            { value: "N/A", label: "N/A" },
          ],
          required: false,
          isCustomField: true,
        },
        '2': {
          varName: "registrationNumber",
          displayText: "Registration Number",
          inputType: "freeText",
          placeholder: "Enter Registration Number",
          required: false,
          isCustomField: true,
        },
      }
    },
  }
};

// Export the configurations for different organizations
export const setupConfig = {
  // Testing / Demos
  default: defaultConfig,
  "ThinkDivergent": defaultConfig,
  "ThinkDivergent Admin": defaultConfig,
  // Customers
  "Phoenix Mental Health Services": defaultConfig,
  "Well Life Clinic": defaultConfig,
  // SSO-only organisations
  "3 Borough Adult ADHD and Autism Service": {
    ...defaultConfig,
    ...defaultConfig,
    // Default sign-in policy for this organisation.
    //
    // Production behaviour:
    // - In the dedicated SLaM production project we treat this as SSO-only (invite-based onboarding).
    //
    // Non-production behaviour (dev/staging only):
    // - We optionally allow Org Admins to override the auth mode per invited user for testing/debugging.
    // - This is used to validate the legacy email/password flow without changing production policy.
    authMode: 'with_microsoft_sso',

    // Dev/staging-only UX feature flag:
    // If enabled, the ProviderAccountSetupForm can render an authMode selector for this org
    // (guarded by environment checks so production is unaffected).
    allowAuthModeOverrideInNonProd: true,

    // Restrict which modes are selectable when the override is enabled.
    // We intentionally keep the surface area small: "SSO-first invite flow" vs "normal hybrid flow".
    authModeOptions: ['with_microsoft_sso', 'hybrid'],
  },
};

// Helper function to get the right config based on organization
export const getConfigProviderAccountSetup = (organizationName) => {
  return setupConfig[organizationName] || setupConfig.default;
};